import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeleteSetupIntentsMutation,
    useSetupIntentsInfiniteQuery,
} from './useSetupIntentsQuery';
import {useSetupIntentsSearch} from './useSetupIntentsSearch';
import {useSetupIntentsTableColumns} from './useSetupIntentsTableColumns';
import {SetupIntentsFilters} from './useSetupIntentsFilter';
import {SetupIntent} from '../types';

/**
 * Default sort: NEWEST FIRST, on `createdAtMillis`.
 *
 * The key must be immutable and non-null. `created` is set by Stripe when the
 * setup intent is created and never moves, so it satisfies both.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<SetupIntent> = {
    columnAccessor: 'createdAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against.
 *
 * ⚠️ `clientSecret` IS DELIBERATELY NOT MATCHED. It is selected by the list
 * query (the module's own confirm flow needs it) but it is the credential that
 * authorises confirming a setup intent from a browser. A value that is not
 * matched cannot be probed by typing guesses into the search box (§11).
 *
 * `lastSetupError` is likewise not matched: it is free-form vendor text, so it
 * would make unrelated rows appear for common words like "card" or "declined".
 */
const matchesKeyword = (item: SetupIntent, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.customerId,
        item.description,
        item.paymentMethod,
        item.status,
        item.usage,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter.
 *
 * ⚠️ EVERY FILTER HERE IS CLIENT-SIDE. `SetupIntentsFilterInput` declares
 * `{customerId: String}` and nothing else, so status and usage cannot be
 * narrowed by the API — see useSetupIntentsFilter for why they must not be sent.
 * `customerId` is excluded because it IS applied server-side, as the query's
 * filter argument; re-applying it here would be harmless but would suggest the
 * narrowing happens over the loaded window when the server already did it.
 */
const matchesFilters = (
    item: SetupIntent,
    filters: SetupIntentsFilters,
): boolean =>
    Object.entries(filters)
          .filter(([key]) => key !== 'customerId')
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof SetupIntent];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ MONEY PATH. A SetupIntent authorises FUTURE off-session charges. Only the
 * LIST/TABLE layer is touched: no confirm/cancel semantics, no idempotency key,
 * no `clientSecret` handling.
 *
 * ⚠️ SCOPE OF SORT, KEYWORD AND FILTERS: the pages FETCHED SO FAR.
 *
 * `customerId` is the one narrowing the SERVER performs. Everything else — the
 * keyword, the status and usage filters, and the sort — runs over the loaded
 * window only, because the API takes no search term, no sort argument and no
 * other filter field. Sorting page 1 does not sort the dataset.
 *
 * That is a property of the token-pagination model, not a bug — and it is why
 * the footer states a LOADED count and never a total.
 */
export const useSetupIntents = (
    customerId?: string,
    filters: SetupIntentsFilters = {},
) => {
    const query = useSetupIntentsInfiniteQuery(customerId, filters);
    const {columns} = useSetupIntentsTableColumns();
    const {keyword} = useSetupIntentsSearch();
    const deleteMutation = useDeleteSetupIntentsMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: SetupIntent[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // ⚠️ FNP-1106. Normalise Stripe's epoch SECONDS into millis ONCE, here,
        // so the Created column can both render and sort the same value.
        // `created` stays untouched as the source of truth.
        //
        // The typeof guard is not ceremony: `created: Int` is NULLABLE in the
        // schema, and `undefined * 1000` is NaN — which moment would render as
        // "Invalid date" rather than the em-dash placeholder.
        .map(item => ({
            ...item,
            createdAtMillis: typeof item.created === 'number'
                ? item.created * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<SetupIntent>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched` is
    // rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        // ⚠️ THIS FETCHES THE NEXT PAGE — it does NOT reload the list. The name
        // is kept because the pagination control calls it, but nothing that
        // means "refresh" may be wired to it: under the old `useQuery` this name
        // invalidated the list, and ReloadRecordsButton still called it. Reload
        // now invalidates the module key directly.
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
